/**
 * Admin Setup — Client-Facing Settings
 *
 * Sidebar entry: "Admin Setup" (visible to group admins, site admins, Helix)
 * Contains settings that client administrators manage: branding, hierarchy,
 * legal, performance, quality, purchasing, onboarding, notifications, regional.
 * Organised with an internal left sidebar for section navigation.
 */
import { useState } from 'react';
import {
  Palette, FolderTree, Scale, ShieldCheck, ShoppingCart, UserPlus,
  TrendingUp, Bell, Globe, PackageCheck, LineChart, ChevronRight, Lock,
} from 'lucide-react';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import PerformanceSettings from '../components/settings/PerformanceSettings';
import {
  useAdminSettings,
  BrandingTab,
  HierarchyTab,
  LegalTab,
  QualityTab,
  QualityTabsTab,
  PurchasingTab,
  PurchasingTabsTab,
  ForecastSettingsTab,
  OnboardingTab,
  NotificationDefaultsTab,
  CallOffConfigTab,
  RegionalTab,
  SecurityTab,
} from '../components/admin/settings';

type Section =
  | 'branding' | 'hierarchy' | 'regional' | 'legal' | 'security'
  | 'performance' | 'quality' | 'purchasing' | 'call-off'
  | 'forecast' | 'onboarding' | 'notifications';

interface SectionDef {
  key: Section;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  group: string;
  featureFlag?: string;
}

const SECTIONS: SectionDef[] = [
  // Company & Branding
  { key: 'branding', label: 'Branding', icon: Palette, group: 'Company' },
  { key: 'hierarchy', label: 'Organisation', icon: FolderTree, group: 'Company' },
  { key: 'regional', label: 'Regional Settings', icon: Globe, group: 'Company' },
  { key: 'legal', label: 'Legal & Policies', icon: Scale, group: 'Company' },
  { key: 'security', label: 'Security', icon: Lock, group: 'Company' },
  // Operations
  { key: 'performance', label: 'Performance', icon: TrendingUp, group: 'Operations' },
  { key: 'quality', label: 'Quality', icon: ShieldCheck, group: 'Operations' },
  { key: 'purchasing', label: 'Purchasing', icon: ShoppingCart, group: 'Operations' },
  { key: 'call-off', label: 'Call-Off', icon: PackageCheck, group: 'Operations', featureFlag: 'switch_call_off_tab' },
  { key: 'forecast', label: 'Forecast', icon: LineChart, group: 'Operations' },
  { key: 'onboarding', label: 'Onboarding', icon: UserPlus, group: 'Operations' },
  // Communications
  { key: 'notifications', label: 'Notifications', icon: Bell, group: 'Communications' },
];

export default function AdminSetup() {
  const { isFeatureEnabled } = useMenuSettings();
  const [activeSection, setActiveSection] = useState<Section>('branding');

  const {
    themeSettings, setThemeSettings,
    platformSettings, setPlatformSettings,
    barrattFlags, setBarrattFlags,
    purchasingTabs, setPurchasingTabs, savePurchasingTabs,
    qualityTabs, setQualityTabs, saveQualityTabs,
    siteSettings, choices, loadingSites, savingSite, editedSites,
    saving,
    saveThemeSettings, savePlatformSettings,
    handleFileUpload, resetColorsToDefault,
    handleSiteSettingChange, saveSiteSettings,
  } = useAdminSettings();

  const visibleSections = SECTIONS.filter(s => {
    if (s.featureFlag && !isFeatureEnabled(s.featureFlag as Parameters<typeof isFeatureEnabled>[0])) return false;
    return true;
  });

  const groups = Array.from(new Set(visibleSections.map(s => s.group)));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Admin Setup</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Company settings, module configuration, and policies
        </p>
      </div>

      <div className="flex gap-6">
        {/* Left sidebar with grouped sections — sticky so it stays while scrolling */}
        <nav className="w-52 shrink-0 space-y-4 sticky top-6 self-start max-h-[calc(100vh-8rem)] overflow-y-auto">
          {groups.map(group => (
            <div key={group}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-1">{group}</p>
              <div className="space-y-0.5">
                {visibleSections.filter(s => s.group === group).map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setActiveSection(key)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                      activeSection === key
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{label}</span>
                    {activeSection === key && <ChevronRight className="h-3 w-3 ml-auto" />}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Content area */}
        <div className="flex-1 min-w-0">
          {activeSection === 'branding' && (
            <BrandingTab
              themeSettings={themeSettings} saving={saving}
              onThemeSettingsChange={setThemeSettings} onSave={saveThemeSettings}
              onResetColors={resetColorsToDefault} onFileUpload={handleFileUpload}
            />
          )}
          {activeSection === 'hierarchy' && <HierarchyTab />}
          {activeSection === 'regional' && (
            <RegionalTab
              siteSettings={siteSettings} choices={choices}
              loadingSites={loadingSites} savingSite={savingSite}
              editedSites={editedSites}
              onSiteSettingChange={handleSiteSettingChange}
              onSaveSiteSettings={saveSiteSettings}
            />
          )}
          {activeSection === 'legal' && <LegalTab />}
          {activeSection === 'security' && <SecurityTab />}
          {activeSection === 'performance' && <PerformanceSettings />}
          {activeSection === 'quality' && (
            <div className="space-y-6">
              <QualityTab />
              <QualityTabsTab qualityTabs={qualityTabs} saving={saving} onQualityTabsChange={setQualityTabs} onSave={saveQualityTabs} />
            </div>
          )}
          {activeSection === 'purchasing' && (
            <div className="space-y-6">
              <PurchasingTab />
              <PurchasingTabsTab purchasingTabs={purchasingTabs} saving={saving} onPurchasingTabsChange={setPurchasingTabs} onSave={savePurchasingTabs} />
            </div>
          )}
          {activeSection === 'call-off' && <CallOffConfigTab />}
          {activeSection === 'forecast' && (
            <ForecastSettingsTab barrattFlags={barrattFlags} saving={saving} onBarrattFlagsChange={setBarrattFlags} onSave={savePlatformSettings} />
          )}
          {activeSection === 'onboarding' && (
            <OnboardingTab
              onboardingEnabled={platformSettings.supplierOnboardingEnabled}
              onToggleOnboarding={(enabled) => {
                setPlatformSettings({ ...platformSettings, supplierOnboardingEnabled: enabled });
                savePlatformSettings();
              }}
            />
          )}
          {activeSection === 'notifications' && (
            <NotificationDefaultsTab platformSettings={platformSettings} saving={saving} onPlatformSettingsChange={setPlatformSettings} onSave={savePlatformSettings} />
          )}
        </div>
      </div>
    </div>
  );
}
